import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {CompassIcon} from '../icons';
import {
    OnboardingState,
    OnboardingStatus,
    fetchOnboardingState,
    isOnboardingClosed,
    skipOnboarding,
} from '../../support/onboardingApi';

const KNOWN_STATUSES: OnboardingStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'SKIPPED'];

/** Ответ похож на состояние мастера: защита от заглушек и чужих ответов на этом адресе. */
const isValidState = (state: OnboardingState | null): state is OnboardingState =>
    Boolean(state) && KNOWN_STATUSES.includes((state as OnboardingState).status);

/**
 * Точка входа в мастер онбординга с главной (CTHS-216 AC-11, CTHS-217 AC-9/AC-10).
 *
 * - мастер не начинали → уводим в мастер сразу: это первый вход;
 * - мастер прерван (`IN_PROGRESS`) → предлагаем продолжить с сохранённого шага, не навязывая
 *   переход: человек мог осознанно уйти в приложение;
 * - `COMPLETED`/`SKIPPED` → баннера нет и больше не будет.
 *
 * Состояние берётся из кэша `onboardingApi`, поэтому возврат на главную не порождает запрос.
 */
const OnboardingResumeBanner: React.FC = () => {
    const navigate = useNavigate();
    const [state, setState] = useState<OnboardingState | null>(null);
    const [dismissing, setDismissing] = useState(false);
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchOnboardingState()
            .then(data => {
                if (cancelled || !isValidState(data)) {
                    return;
                }
                if (isOnboardingClosed(data)) {
                    return;
                }
                if (!data.configured || data.status === 'NOT_STARTED') {
                    navigate('/onboarding', {replace: true});
                    return;
                }
                setState(data);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const dismiss = async () => {
        setDismissing(true);
        try {
            await skipOnboarding();
            setHidden(true);
        } catch {
            setDismissing(false);
        }
    };

    if (hidden || !state) {
        return null;
    }

    return (
        <section
            className="card p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid="onboarding-resume-banner"
        >
            <div className="flex items-start gap-3">
                <CompassIcon className="w-5 h-5 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5"/>
                <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Продолжить настройку
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Настройка стилиста не закончена — продолжим с того шага, где вы остановились.
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={dismiss}
                    disabled={dismissing}
                    className="min-h-[44px] px-3 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                    data-testid="onboarding-resume-dismiss"
                >
                    Больше не предлагать
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/onboarding')}
                    className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                    data-testid="onboarding-resume-continue"
                >
                    Продолжить
                </button>
            </div>
        </section>
    );
};

export default OnboardingResumeBanner;
